import React, { useState } from 'react';
import { Field, FieldProps } from 'formik';
import { useI18n } from 'i18n/I18nContext';

export interface InputProps {
  label: string;
  name: string;
  type: string;
  validate?: (value: string) => string | undefined;
  error?: string | boolean;
  hint?: string;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  /** Show a Show/Hide control for password fields. */
  revealToggle?: boolean;
}

const Input = ({
  label,
  name,
  type,
  validate,
  error = false,
  hint,
  autoComplete,
  inputMode,
  revealToggle = false,
}: InputProps) => {
  const { t } = useI18n();
  const [revealed, setRevealed] = useState(false);
  const errorId = `${name}-error`;
  const hintId = `${name}-hint`;
  const hasErrorMessage = Boolean(error && typeof error === 'string');
  const describedBy = [hint ? hintId : null, hasErrorMessage ? errorId : null]
    .filter(Boolean)
    .join(' ');

  const isPassword = type === 'password';
  const showToggle = revealToggle && isPassword;
  const inputType = showToggle && revealed ? 'text' : type;

  const baseClass =
    'py-10 bg-transparent border-b border-grey font-light text-white focus:border-b-2 gb-input ';
  const errorClass = error ? 'border-red' : '';
  const inputWithToggleClass = showToggle ? 'gb-input--with-toggle ' : '';

  return (
    <div className="gb-onboard-field">
      <label htmlFor={name} className="text-sm font-normal gb-form-label">
        {label}
      </label>
      <div className={showToggle ? 'gb-onboard-field__control' : undefined}>
        <Field
          name={name}
          validate={validate}
          render={({ field }: FieldProps) => (
            <input
              {...field}
              id={name}
              type={inputType}
              className={`${inputWithToggleClass}${baseClass}${errorClass}`.trim()}
              autoComplete={autoComplete}
              inputMode={inputMode}
              aria-invalid={error ? true : undefined}
              aria-describedby={describedBy || undefined}
            />
          )}
        />
        {showToggle ? (
          <button
            type="button"
            className="gb-onboard-field__reveal"
            aria-pressed={revealed}
            aria-controls={name}
            onClick={() => setRevealed((v) => !v)}
          >
            {revealed ? t('forms.hidePassword') : t('forms.showPassword')}
          </button>
        ) : null}
      </div>

      {hint && !hasErrorMessage ? (
        <p id={hintId} className="gb-form-hint text-xs m-0">
          {hint}
        </p>
      ) : null}

      {hasErrorMessage ? (
        <div id={errorId} className="text-red text-xs" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
};

export default Input;



// WEBPACK FOOTER //
// ./src/components/Input.tsx